import Sidebar from 'components/layout/Sidebar';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import PayrollReceiptWrapper from 'features/report/pages/ReportPage/EmployeeSalary/Previews/PayrollReceiptWrapper';
import Text from 'components/Text';
import Box from 'components/Box';
import { formatCurrency } from 'utils/formatCurrency';
import ticketListApis from 'features/ticketList/services/apis';
import { useEffect, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import { IDejavooReportSummaryResData } from 'features/ticketList/services/types/creditCard';
import { useReactToPrint } from 'react-to-print';
import CreditCardPrintSummaryHeader from './CreditCardPrintSummaryHeader';

const CreditCardDailyReportSummaryPage = () => {
  const [dejavooReportData, setDejavooReportData] = useState<IDejavooReportSummaryResData | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const startDate = location.state?.startDate;

  const printRef = useRef<HTMLDivElement>(null);
  const onPrint = useReactToPrint({
    content: () => printRef.current,
    copyStyles: true,
  });

  const onBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const getDejavooDailyReport = async () => {
      try {
        const response: IResponseDataBody<IDejavooReportSummaryResData> =
          await ticketListApis.getDejavooDailySummary(startDate);

        if (response?.data.data) {
          setDejavooReportData(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching Dejavoo daily report:', error);
        throw error;
      }
    };
    getDejavooDailyReport();
  }, []);

  const handlePrintOverview = () => {
    if (!printRef.current) {
      // @ts-ignore
      printRef.current.style.display = 'block';
    }
    setTimeout(() => onPrint(), 1);
  };

  return (
    <Wrapper>
      <Sidebar />
      <Container>
        <CreditCardPrintSummaryHeader onBack={onBack} onPrint={handlePrintOverview} />
        <div ref={printRef}>
          <PrintContainer>
            <PayrollReceiptWrapper>
              <Content>
                <Text variant='H5' className='header-print' printMode wrapWidth='200px'>
                  Total CR/DB Daily Batch Reports
                </Text>
                <Daily>
                  <Text variant='H9' printMode>
                    {startDate}
                  </Text>
                </Daily>
                <TableStyled>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'start' }}>TERMINAL</th>
                      <th style={{ textAlign: 'center' }}>BATCH NUMBER</th>
                      <th style={{ textAlign: 'right' }}>AMOUNT</th>
                    </tr>
                  </thead>

                  <tbody>
                    {dejavooReportData?.lstStations?.map((item, i) => (
                      <tr key={i}>
                        <td className='ticket-col'>#{item?.stationNumber}</td>
                        <td className='center'>{item?.batchNumber}</td>
                        <td className='right'>{formatCurrency(item?.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </TableStyled>
                <Box width='100%' display='flex' flexDirection='column' style={{ borderTop: '1px dashed #000', paddingTop: '4px'}}>
                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>
                      Records:
                    </Text>
                    <Text variant='CAPTION_2' printMode>
                      {dejavooReportData?.totalTransactions}
                    </Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>
                      Total:
                    </Text>
                    <Text variant='CAPTION_2' printMode>
                      {formatCurrency(dejavooReportData?.total)}
                    </Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>
                      Total tip:
                    </Text>
                    <Text variant='CAPTION_2' printMode>
                      {formatCurrency(dejavooReportData?.totalTip)}
                    </Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>
                      Total w/o tip:
                    </Text>
                    <Text variant='CAPTION_2' printMode>
                      {formatCurrency(dejavooReportData?.totalWithoutTip)}
                    </Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>
                      Non Cash Adj Total:
                    </Text>
                    <Text variant='CAPTION_2' printMode>
                      {formatCurrency(dejavooReportData?.nonCashAdjustment)}
                    </Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>
                      TOTAL w/o Non Cash Adj:
                    </Text>
                    <Text variant='CAPTION_2' printMode>
                      {formatCurrency(dejavooReportData?.totalWithoutAdjustment)}
                    </Text>
                  </InfoRow>

                  <InfoRow>
                    <Text variant='CAPTION_4' printMode style={{ fontWeight: 600 }}>
                      TOTAL w/o fee/tip:
                    </Text>
                    <Text variant='CAPTION_2' printMode>
                      {formatCurrency(dejavooReportData?.totalWithoutFeeAndTip)}
                    </Text>
                  </InfoRow>
                </Box>

                <Center>
                  <Text variant='CAPTION_4' style={{ fontWeight: 500 }} printMode>
                    End of report - Powered by Fozito
                  </Text>
                </Center>
              </Content>
            </PayrollReceiptWrapper>
          </PrintContainer>
        </div>
      </Container>
    </Wrapper>
  );
};

export default CreditCardDailyReportSummaryPage;

const Wrapper = styled.div`
  display: flex;
`;

const Container = styled.div`
  background-color: #f1f5f9;
  flex-grow: 1;
  height: 100vh;
  overflow: auto;
  position: relative;
  width: 100%;

  .print-text {
    padding: 0;
  }
`;

const PrintContainer = styled.div`
  .styled-print {
    padding-top: 24px;
    padding-bottom: 24px;
    border-radius: 4px;
  }
`;

const Content = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;

  .header-print {
    text-align: center;
    font-family: 'Montserrat';
    font-size: 20px;
    font-weight: 800;
    line-height: 1;
  }
`;

const Daily = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px dashed #000;
  border-bottom: 1px dashed #000;
  margin: 4px 0px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;

  .CAPTION_2 {
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
  }

  .CAPTION_4 {
    line-height: 1;
  }
`;

const Center = styled.div`
  width: 100%;
  text-align: center;
  border-bottom: 1px dashed #000;
  padding: 4px 0px;
`;

const TableStyled = styled.table`
  width: 100%;

  thead {
    th {
      font-family: 'Roboto', serif !important;
      color: black !important;
      font-size: 12px;
      font-weight: 600;
      padding-bottom: 4px;
      border-bottom: 1px dashed #000;
    }
  }

  tbody {
    td {
      font-family: 'Roboto', serif !important;
      color: black !important;
      font-size: 10px;
      font-weight: 600;
      line-height: 1;
    }

    :first-child {
      td {
        padding-top: 2px;
      }
    }
  }

  .ticket-col {
    width: 65px;
  }

  .start {
    text-align: start;
  }

  .center {
    text-align: center;
  }

  .right {
    text-align: right;
  }

  .end {
    text-align: end;
  }
`;